import React, { useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import {
  Upload, FileText, Video, Globe, Loader2, X,
  AlertCircle, FileSearch, Trash2, RefreshCw, ChevronDown, Search, Plus, Check,
} from 'lucide-react';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useOrganization } from '@/hooks/useOrganization';
import { useInterviewerSetup } from '@/hooks/useInterviewerSetup';
import { useKnowledgeAssets } from '@/hooks/useKnowledgeAssets';
import { KnowledgeRemovalDialog } from '@/components/onboarding/KnowledgeRemovalDialog';
import {
  detectKnowledgeAssetFileType,
  getKnowledgeAssetTypeLabel,
  isDocumentKnowledgeAssetType,
  type KnowledgeAsset,
} from '@/lib/api/knowledgeAssets';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Status Badge ───────────────────────────────────────────────────────────

function ProcessingBadge({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return (
        <span className="inline-flex items-center gap-1 h-5 px-1.5 py-0.5 rounded text-xs font-medium bg-success/10 text-success">
          <span className="w-1 h-1 rounded-full bg-success" />
          Ready
        </span>
      );
    case 'pending':
    case 'processing':
      return (
        <span className="inline-flex items-center gap-1 h-5 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
          <Loader2 size={10} className="animate-spin" />
          Processing
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 h-5 px-1.5 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300">
          <span className="w-1 h-1 rounded-full bg-red-700 dark:bg-red-300" />
          Failed
        </span>
      );
    default:
      return null;
  }
}

// ─── File Type Icon ─────────────────────────────────────────────────────────

function FileTypeIcon({ type }: { type: string }) {
  if (isDocumentKnowledgeAssetType(type)) {
    return (
      <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
        <FileText size={16} className="text-red-500" />
      </div>
    );
  }
  if (type === 'url') {
    return (
      <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
        <Globe size={16} className="text-purple-500" />
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
      <Video size={16} className="text-blue-500" />
    </div>
  );
}

// ─── Upload / Add Dialog ────────────────────────────────────────────────────

type DialogMode = 'file' | 'url';

const ACCEPTED_TYPES = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/x-markdown',
  'video/mp4',
  'video/quicktime',
  'video/webm',
];
const ACCEPTED_EXTENSIONS = '.pdf,.md,.markdown,.txt,.mp4,.mov,.webm';
const MAX_FILE_SIZE = 100 * 1024 * 1024;

function AddAssetDialog({
  open,
  onOpenChange,
  onUploadFiles,
  onAddUrl,
  isUploading,
  isAddingUrl,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onUploadFiles: (files: File[]) => Promise<void>;
  onAddUrl: (url: string, title?: string, urlType?: 'landing_page' | 'documentation') => Promise<void>;
  isUploading: boolean;
  isAddingUrl: boolean;
}) {
  const [mode, setMode] = useState<DialogMode>('file');
  const [files, setFiles] = useState<File[]>([]);
  const [url, setUrl] = useState('');
  const [urlTitle, setUrlTitle] = useState('');
  const [urlType, setUrlType] = useState<'landing_page' | 'documentation'>('landing_page');
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isBusy = isUploading || isAddingUrl;

  const resetForm = () => {
    setFiles([]);
    setUrl('');
    setUrlTitle('');
    setUrlType('landing_page');
    setError(null);
  };

  const handleClose = () => {
    if (!isBusy) {
      resetForm();
      onOpenChange(false);
    }
  };

  const validateFile = (f: File): string | null => {
    const detectedType = detectKnowledgeAssetFileType(f);
    if (!detectedType && f.type && !ACCEPTED_TYPES.includes(f.type)) return `${f.name}: invalid file type`;
    if (!detectedType) return `${f.name}: invalid file type`;
    if (detectedType === 'pdf' && f.size > MAX_FILE_SIZE) return `${f.name}: exceeds 100MB limit`;
    return null;
  };

  const handleFilesSelect = (incoming: FileList | File[]) => {
    const arr = Array.from(incoming);
    const errors: string[] = [];
    const valid: File[] = [];
    for (const f of arr) {
      const err = validateFile(f);
      if (err) errors.push(err);
      else valid.push(f);
    }
    if (errors.length) setError(errors.join('. '));
    else setError(null);
    setFiles(prev => [...prev, ...valid]);
  };

  const removeFile = (i: number) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) handleFilesSelect(e.dataTransfer.files);
  };

  const handleSubmit = async () => {
    if (mode === 'file') {
      if (files.length === 0) { setError('Please select at least one file.'); return; }
      try {
        await onUploadFiles(files);
        resetForm();
        onOpenChange(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
      }
    } else {
      if (!url.trim()) { setError('Please enter a URL.'); return; }
      try {
        await onAddUrl(url.trim(), urlTitle || undefined, urlType);
        resetForm();
        onOpenChange(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add URL');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Source</DialogTitle>
          <DialogDescription>
            Upload documents, videos, or add a URL to your knowledge base.
          </DialogDescription>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="flex gap-1 p-0.5 bg-muted rounded-lg w-fit">
          <button
            onClick={() => { setMode('file'); setError(null); }}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              mode === 'file' ? 'bg-white dark:bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Upload Files
          </button>
          <button
            onClick={() => { setMode('url'); setError(null); }}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              mode === 'url' ? 'bg-white dark:bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            URL
          </button>
        </div>

        <div className="space-y-4 py-2">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {mode === 'file' ? (
            <>
              <div
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                  isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50'
                )}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_EXTENSIONS}
                  multiple
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) handleFilesSelect(e.target.files);
                    e.target.value = '';
                  }}
                  className="hidden"
                />
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">Drag & drop files here, or click to browse</p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, Markdown, Text, MP4, MOV, or WebM. Select multiple files at once.
                </p>
              </div>

              {files.length > 0 && (
                <div className="space-y-1 max-h-[180px] overflow-y-auto">
                  {files.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/50 text-sm">
                      <FileTypeIcon type={detectKnowledgeAssetFileType(f) || 'unknown'} />
                      <span className="flex-1 truncate text-foreground font-medium">{f.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{formatFileSize(f.size)}</span>
                      <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-foreground shrink-0 p-0.5">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="asset-url">URL</Label>
                <Input id="asset-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/docs" />
              </div>
              <div className="space-y-2">
                <Label>URL Type</Label>
                <div className="flex gap-1 p-0.5 bg-muted rounded-lg w-fit">
                  <button onClick={() => setUrlType('landing_page')} className={cn('px-3 py-1.5 text-sm font-medium rounded-md transition-colors', urlType === 'landing_page' ? 'bg-white dark:bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground')}>Landing Page</button>
                  <button onClick={() => setUrlType('documentation')} className={cn('px-3 py-1.5 text-sm font-medium rounded-md transition-colors', urlType === 'documentation' ? 'bg-white dark:bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground')}>Documentation</button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {urlType === 'landing_page' ? 'Extracts content from this single page.' : 'Crawls the site to discover and extract multiple pages.'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="url-title">Title (optional)</Label>
                <Input id="url-title" value={urlTitle} onChange={(e) => setUrlTitle(e.target.value)} placeholder="Enter a display name" />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isBusy}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isBusy || (mode === 'file' ? files.length === 0 : !url.trim())}>
            {isBusy ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{mode === 'file' ? 'Uploading...' : 'Adding...'}</>
            ) : (
              mode === 'file' ? `Upload ${files.length || ''} File${files.length !== 1 ? 's' : ''}`.replace('Upload  Files', 'Upload') : 'Add URL'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Facets Display ─────────────────────────────────────────────────────────

function facetLabel(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj.name === 'string') return obj.name;
    if (typeof obj.label === 'string') return obj.label;
    return JSON.stringify(value);
  }
  return String(value);
}

function FacetTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs text-muted-foreground">
      {children}
    </span>
  );
}

function FacetLabelValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-muted-foreground shrink-0 w-24">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function FacetsSection({ facets }: { facets: Record<string, any> }) {
  const [expanded, setExpanded] = useState(true);

  const product = facets.product;
  const features = facets.features as any[] | undefined;
  const valueProps = facets.value_propositions as any[] | undefined;
  const useCases = facets.use_cases as any[] | undefined;
  const audience = facets.target_audience;
  const messaging = facets.messaging;
  const pricing = facets.pricing;
  const objections = facets.objections as any[] | undefined;
  const integrations = facets.integrations as string[] | undefined;

  const hasContent =
    product?.name ||
    (features && features.length > 0) ||
    (valueProps && valueProps.length > 0) ||
    (useCases && useCases.length > 0) ||
    audience?.primary_personas?.length ||
    messaging?.tone ||
    pricing?.model ||
    (objections && objections.length > 0) ||
    (integrations && integrations.length > 0);

  if (!hasContent) return null;

  return (
    <div className="border-b border-border">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full p-4 text-left hover:bg-muted/30 transition-colors"
      >
        <span className="text-xs font-semibold text-foreground">Extracted Information</span>
        <ChevronDown
          size={14}
          className={cn('text-muted-foreground transition-transform', expanded && 'rotate-180')}
        />
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Product */}
          {product?.name && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Product</p>
              <div className="space-y-1">
                <FacetLabelValue label="Name" value={product.name} />
                {product.description && <FacetLabelValue label="Description" value={product.description} />}
                {product.category && <FacetLabelValue label="Category" value={product.category} />}
                {product.primary_function && <FacetLabelValue label="Function" value={product.primary_function} />}
              </div>
            </div>
          )}

          {/* Features */}
          {features && features.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Features ({features.length})</p>
              <div className="space-y-1.5">
                {features.map((f, i) => (
                  <div key={i} className="text-xs">
                    <span className="font-medium text-foreground">{f.name}</span>
                    {f.description && (
                      <span className="text-muted-foreground"> — {f.description}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Value Propositions */}
          {valueProps && valueProps.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Value Propositions</p>
              <div className="space-y-1.5">
                {valueProps.map((vp, i) => (
                  <div key={i} className="text-xs">
                    <span className="text-foreground">{vp.statement}</span>
                    {vp.benefit && (
                      <span className="text-muted-foreground"> — {vp.benefit}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Use Cases */}
          {useCases && useCases.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Use Cases</p>
              <div className="space-y-1.5">
                {useCases.map((uc, i) => (
                  <div key={i} className="text-xs">
                    <span className="font-medium text-foreground">{uc.scenario}</span>
                    {uc.solution && (
                      <span className="text-muted-foreground"> — {uc.solution}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Target Audience */}
          {audience && (audience.primary_personas?.length > 0 || audience.industries?.length > 0 || audience.pain_points?.length > 0) && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Target Audience</p>
              {audience.primary_personas?.length > 0 && (
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground">Personas</span>
                  <div className="flex flex-wrap gap-1">
                    {audience.primary_personas.map((p: unknown, i: number) => (
                      <FacetTag key={i}>{facetLabel(p)}</FacetTag>
                    ))}
                  </div>
                </div>
              )}
              {audience.industries?.length > 0 && (
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground">Industries</span>
                  <div className="flex flex-wrap gap-1">
                    {audience.industries.map((ind: unknown, i: number) => (
                      <FacetTag key={i}>{facetLabel(ind)}</FacetTag>
                    ))}
                  </div>
                </div>
              )}
              {audience.pain_points?.length > 0 && (
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground">Pain Points</span>
                  <ul className="space-y-0.5 pl-3">
                    {audience.pain_points.map((pp: unknown, i: number) => (
                      <li key={i} className="text-xs text-muted-foreground list-disc">{facetLabel(pp)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Messaging */}
          {messaging && (messaging.tone || messaging.positioning || messaging.key_phrases?.length > 0) && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Messaging</p>
              <div className="space-y-1">
                {messaging.tone && <FacetLabelValue label="Tone" value={messaging.tone} />}
                {messaging.positioning && <FacetLabelValue label="Positioning" value={messaging.positioning} />}
              </div>
              {messaging.key_phrases?.length > 0 && (
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground">Key Phrases</span>
                  <div className="flex flex-wrap gap-1">
                    {messaging.key_phrases.map((kp: unknown, i: number) => (
                      <FacetTag key={i}>{facetLabel(kp)}</FacetTag>
                    ))}
                  </div>
                </div>
              )}
              {messaging.differentiators?.length > 0 && (
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground">Differentiators</span>
                  <ul className="space-y-0.5 pl-3">
                    {messaging.differentiators.map((d: unknown, i: number) => (
                      <li key={i} className="text-xs text-muted-foreground list-disc">{facetLabel(d)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Pricing */}
          {pricing && (pricing.model || pricing.pricing_info) && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Pricing</p>
              <div className="space-y-1">
                {pricing.model && <FacetLabelValue label="Model" value={pricing.model} />}
                {pricing.pricing_info && <FacetLabelValue label="Details" value={pricing.pricing_info} />}
              </div>
              {pricing.tiers_mentioned?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {pricing.tiers_mentioned.map((t: unknown, i: number) => (
                    <FacetTag key={i}>{facetLabel(t)}</FacetTag>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Objections */}
          {objections && objections.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Objections ({objections.length})</p>
              <div className="space-y-2">
                {objections.map((obj, i) => (
                  <div key={i} className="text-xs space-y-0.5">
                    <p className="font-medium text-foreground">"{obj.objection}"</p>
                    {obj.response && (
                      <p className="text-muted-foreground pl-2 border-l-2 border-border">
                        {obj.response}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Integrations */}
          {integrations && integrations.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Integrations</p>
              <div className="flex flex-wrap gap-1">
                {integrations.map((integ, i) => (
                  <FacetTag key={i}>{facetLabel(integ)}</FacetTag>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Asset Detail Panel ─────────────────────────────────────────────────────

function AssetDetailPanel({
  asset,
  onClose,
  onSaveSummary,
  onRefresh,
  onDelete,
  isRefreshing,
}: {
  asset: KnowledgeAsset;
  onClose: () => void;
  onSaveSummary: (summary: string) => Promise<void>;
  onRefresh: () => void;
  onDelete: () => void;
  isRefreshing: boolean;
}) {
  const [editedSummary, setEditedSummary] = useState(asset.summary || '');
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isDirty = editedSummary !== (asset.summary || '');

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSaveSummary(editedSummary);
      setIsEditingSummary(false);
    } finally {
      setIsSaving(false);
    }
  };

  const displayName = asset.title || asset.filename || asset.url || 'Untitled';
  const metaParts: string[] = [
    getKnowledgeAssetTypeLabel(asset.asset_type, asset.url_type).toUpperCase(),
  ];
  if (asset.file_size_bytes) metaParts.push(formatFileSize(asset.file_size_bytes));
  metaParts.push(format(new Date(asset.created_at), 'MMM d, yyyy'));

  return (
    <motion.div
      initial={{ x: 40, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 40, opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="w-[360px] shrink-0 border border-border bg-card overflow-y-auto rounded-lg"
    >
      {/* Header — source name + actions */}
      <div className="flex items-start gap-2 p-4 border-b border-border">
        <FileTypeIcon type={asset.asset_type} />
        <div className="flex-1 min-w-0 pt-0.5">
          <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
          <div className="flex items-center gap-1.5 mt-1">
            <ProcessingBadge status={asset.processing_status} />
            <span className="text-[11px] text-muted-foreground">{metaParts.join(' · ')}</span>
          </div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {asset.asset_type === 'url' && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={onRefresh} disabled={isRefreshing} title="Refresh">
              <RefreshCw size={14} className={cn(isRefreshing && 'animate-spin')} />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={onDelete} title="Delete">
            <Trash2 size={14} />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={onClose} title="Close">
            <X size={14} />
          </Button>
        </div>
      </div>

      {/* URL (if applicable) */}
      {asset.url && (
        <div className="px-4 py-2.5 border-b border-border">
          <a href={asset.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline truncate block">
            {asset.url}
          </a>
        </div>
      )}

      {/* Summary — read-first, click to edit */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Summary</span>
          {isEditingSummary && isDirty && (
            <Button size="sm" variant="outline" className="h-5 text-[10px] px-2 rounded" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Loader2 size={10} className="animate-spin" /> : 'Save'}
            </Button>
          )}
          {isEditingSummary && !isDirty && (
            <button onClick={() => setIsEditingSummary(false)} className="text-[10px] text-muted-foreground hover:text-foreground">Done</button>
          )}
        </div>
        {isEditingSummary ? (
          <Textarea
            value={editedSummary}
            onChange={(e) => setEditedSummary(e.target.value)}
            placeholder="No summary yet..."
            rows={4}
            className="text-xs"
            autoFocus
          />
        ) : (
          <p
            className="text-xs text-muted-foreground leading-relaxed cursor-pointer hover:text-foreground transition-colors"
            onClick={() => setIsEditingSummary(true)}
            title="Click to edit"
          >
            {asset.summary || 'No summary yet. Click to add one.'}
          </p>
        )}
      </div>

      {/* Extracted Facets — expanded by default */}
      {asset.facets && Object.keys(asset.facets).length > 0 && (
        <FacetsSection facets={asset.facets} />
      )}
    </motion.div>
  );
}

// ─── Assets Table ───────────────────────────────────────────────────────────

function AssetsTable({
  assets,
  selectedId,
  onRowClick,
  onDelete,
  onRefresh,
  isRefreshing,
}: {
  assets: KnowledgeAsset[];
  selectedId: string | null;
  onRowClick: (a: KnowledgeAsset) => void;
  onDelete: (id: string) => void;
  onRefresh: (id: string) => void;
  isRefreshing: boolean;
}) {
  if (assets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
          <FileSearch className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-foreground mb-1">No sources yet</p>
        <p className="text-sm text-muted-foreground max-w-[280px]">
          Upload documents, videos, or add URLs to build your knowledge base.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-b border-border">
            <TableHead className="h-8 px-3 py-1.5 text-xs font-medium text-muted-foreground">Name</TableHead>
            <TableHead className="h-8 px-3 py-1.5 text-xs font-medium text-muted-foreground">Status</TableHead>
            <TableHead className="h-8 px-3 py-1.5 text-xs font-medium text-muted-foreground">Added</TableHead>
            <TableHead className="h-8 px-3 py-1.5 text-xs font-medium text-muted-foreground w-[60px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {assets.map((asset) => (
            <TableRow
              key={asset.id}
              className={cn(
                'cursor-pointer border-b border-border transition-colors',
                selectedId === asset.id ? 'bg-accent/50' : 'hover:bg-muted/30'
              )}
              onClick={() => onRowClick(asset)}
            >
              <TableCell className="px-3 py-2">
                <div className="flex items-center gap-2.5">
                  <FileTypeIcon type={asset.asset_type} />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-foreground truncate block max-w-[300px]">
                      {asset.title || asset.filename || asset.url || 'Untitled'}
                    </span>
                    <span className="text-xs text-muted-foreground uppercase">
                      {getKnowledgeAssetTypeLabel(asset.asset_type, asset.url_type)}
                      {asset.file_size_bytes ? ` · ${formatFileSize(asset.file_size_bytes)}` : ''}
                    </span>
                  </div>
                </div>
              </TableCell>
              <TableCell className="px-3 py-2">
                <ProcessingBadge status={asset.processing_status} />
              </TableCell>
              <TableCell className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                {format(new Date(asset.created_at), 'MMM d, yyyy')}
              </TableCell>
              <TableCell className="px-3 py-2">
                <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                  {asset.asset_type === 'url' && (
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onRefresh(asset.id)} disabled={isRefreshing} title="Refresh">
                      <RefreshCw size={12} />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => onDelete(asset.id)} title="Delete">
                    <Trash2 size={12} />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

const BackgroundPage: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id || '';

  const {
    assets,
    isLoading,
    uploadFile,
    addUrl,
    updateAsset,
    deleteAsset,
    refreshAsset,
    isUploading,
    isAddingUrl,
    isRefreshing,
  } = useKnowledgeAssets(orgId);
  const { interviewers, primaryInterviewer } = useInterviewerSetup(orgId);

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<KnowledgeAsset | null>(null);
  const [trainingLinkedAssetToDelete, setTrainingLinkedAssetToDelete] = useState<KnowledgeAsset | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDraggingOnPage, setIsDraggingOnPage] = useState(false);
  const dragCounterRef = useRef(0);

  const filteredAssets = searchQuery
    ? assets.filter(a => {
        const q = searchQuery.toLowerCase();
        return (a.title || '').toLowerCase().includes(q)
          || (a.filename || '').toLowerCase().includes(q)
          || (a.url || '').toLowerCase().includes(q);
      })
    : assets;

  const handleUploadFiles = async (files: File[]) => {
    for (const file of files) {
      await uploadFile(file);
    }
  };

  const handleAddUrl = async (url: string, title?: string, urlType?: 'landing_page' | 'documentation') => {
    await addUrl(url, title, urlType);
  };

  // Page-level drag and drop
  const handlePageDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) setIsDraggingOnPage(true);
  }, []);
  const handlePageDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDraggingOnPage(false);
  }, []);
  const handlePageDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); }, []);
  const handlePageDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDraggingOnPage(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      for (const file of droppedFiles) {
        const detectedType = detectKnowledgeAssetFileType(file);
        if (detectedType) await uploadFile(file);
      }
    }
  }, [uploadFile]);

  const getAttachedTrainingForAsset = useCallback(
    (assetId: string) => {
      const attached = interviewers.find((interviewer) =>
        (interviewer.knowledge_asset_ids || []).includes(assetId)
      );
      return attached || null;
    },
    [interviewers]
  );

  const handleDelete = async (assetId: string) => {
    const asset = assets.find((item) => item.id === assetId) || null;
    const attachedTraining = getAttachedTrainingForAsset(assetId);

    if (attachedTraining && asset) {
      setTrainingLinkedAssetToDelete(asset);
      return;
    }

    if (selectedAsset?.id === assetId) {
      setSelectedAsset(null);
    }
    await deleteAsset(assetId);
  };

  const attachedTrainingForPendingDelete = trainingLinkedAssetToDelete
    ? getAttachedTrainingForAsset(trainingLinkedAssetToDelete.id)
    : null;

  const handleRefresh = async (assetId: string) => {
    await refreshAsset(assetId);
  };

  const handleSaveSummary = async (summary: string) => {
    if (selectedAsset) {
      await updateAsset(selectedAsset.id, { summary });
      setSelectedAsset((prev) => prev ? { ...prev, summary } : null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Background | Hobbes</title>
      </Helmet>
      <div
        className="flex min-h-0 flex-col h-full relative"
        onDragEnter={handlePageDragEnter}
        onDragLeave={handlePageDragLeave}
        onDragOver={handlePageDragOver}
        onDrop={handlePageDrop}
      >
        <PageHeader title="Background" />

        {/* Drag overlay */}
        {isDraggingOnPage && (
          <div className="absolute inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center pointer-events-none">
            <div className="border-2 border-dashed border-primary rounded-xl p-12 text-center">
              <Upload className="h-10 w-10 mx-auto mb-3 text-primary" />
              <p className="text-lg font-medium text-foreground">Drop files to upload</p>
              <p className="text-sm text-muted-foreground mt-1">PDF, Markdown, Text, MP4, MOV, or WebM</p>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Main area */}
          <div className="flex-1 min-w-0 overflow-y-auto px-4 sm:px-6 md:px-8 pb-8">
            {/* Toolbar */}
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-[280px]">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search sources..."
                  className="h-8 pl-8 text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground">{filteredAssets.length} of {assets.length}</p>
              <div className="flex-1" />
              <Button
                onClick={() => setIsUploadOpen(true)}
                className="h-8 px-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm font-medium tracking-[-0.014em]"
              >
                <Plus size={14} className="mr-1.5" />
                Add Source
              </Button>
            </div>

            {isLoading ? (
              <div className="h-[200px] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <AssetsTable
                assets={filteredAssets}
                selectedId={selectedAsset?.id ?? null}
                onRowClick={setSelectedAsset}
                onDelete={handleDelete}
                onRefresh={handleRefresh}
                isRefreshing={isRefreshing}
              />
            )}
          </div>

          {/* Detail panel */}
          <AnimatePresence mode="wait">
            {selectedAsset && (
              <AssetDetailPanel
                key={selectedAsset.id}
                asset={selectedAsset}
                onClose={() => setSelectedAsset(null)}
                onSaveSummary={handleSaveSummary}
                onRefresh={() => handleRefresh(selectedAsset.id)}
                onDelete={() => handleDelete(selectedAsset.id)}
                isRefreshing={isRefreshing}
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      <AddAssetDialog
        open={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        onUploadFiles={handleUploadFiles}
        onAddUrl={handleAddUrl}
        isUploading={isUploading}
        isAddingUrl={isAddingUrl}
      />

      {trainingLinkedAssetToDelete && (attachedTrainingForPendingDelete || primaryInterviewer) ? (
        <KnowledgeRemovalDialog
          organizationId={orgId}
          interviewerId={(attachedTrainingForPendingDelete || primaryInterviewer)!.id}
          sourceLabel={trainingLinkedAssetToDelete.title || trainingLinkedAssetToDelete.filename || trainingLinkedAssetToDelete.url || 'this source'}
          payload={{ knowledge_asset_ids: [trainingLinkedAssetToDelete.id] }}
          open={!!trainingLinkedAssetToDelete}
          onOpenChange={(open) => {
            if (!open) setTrainingLinkedAssetToDelete(null);
          }}
          title="Delete Source From Background"
          description={
            <>
              Delete <span className="font-medium text-foreground">{trainingLinkedAssetToDelete.title || trainingLinkedAssetToDelete.filename || trainingLinkedAssetToDelete.url || 'this source'}</span> from
              Background and remove it from your training workspace. I&apos;ll preview what knowledge may weaken before anything changes.
            </>
          }
          confirmLabel="Remove And Delete"
          onRemoved={async () => {
            if (selectedAsset?.id === trainingLinkedAssetToDelete.id) {
              setSelectedAsset(null);
            }
            await deleteAsset(trainingLinkedAssetToDelete.id);
            setTrainingLinkedAssetToDelete(null);
          }}
        />
      ) : null}
    </>
  );
};

export default BackgroundPage;
